import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { IconButton } from 'rsuite';
import * as dataActions from '@/data/data-actions';
import * as userSelectors from '@/user/user-selectors';
import styled from 'styled-components';
import {
  LogEventDetailsText,
  LogEventDetailsWrapper,
} from './log-details-common';
import { FaPencilAlt, FaSave, FaBan } from 'react-icons/fa';
const isEditedMessageDict = {
  true: '(edited)',
  false: '',
};

const NotesText = styled(LogEventDetailsText)`
  ::after {
    margin-left: 5px;
    content: ${(props) => `'${isEditedMessageDict[props.edited]}'`};
    font-size: 12px;
    color: lightgray;
  }
`;

const StyledTextArea = styled(TextareaAutosize)`
  width: 100%;
  margin-bottom: 6px; /* same 15px as Styled text, minus 9 pixels occupied by border and padding */
  color: black;
`;

const buttonLabelByEditMode = {
  false: 'edit',
  true: 'save',
};

const buttonIconByLabel = {
  edit: <FaPencilAlt />,
  save: <FaSave />,
};

const NoteLinkingMessage = styled.div`
  ::before {
    content: 'This note is associated with multiple properties, it may not be relevant for this property.';
    opacity: 0.85;
    font-family: Lato;
    font-size: 12px;
    font-style: italic;
    line-height: 1.17;
    color: rgba(0, 0, 0, 0.5);
  }
`;

/**
 * This component renders an ActivityFeed note and allows the user to edit it
 *
 * @param {string} props.message text in note
 * @param {string} props.noteId note id. Useful for updating the correct note
 * @param {string} props.author.email email of user who made the note
 * @param {string} props.author.name name of user who made the note
 * // TODO: use author for only allowing authors to edit their own notes
 */
export function NoteLog({
  index,
  id: noteId,
  message,
  author,
  currentUserEmail,
  edited,
  moved,
}) {
  const [editMode, setEditMode] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const dispatch = useDispatch();
  const userRights = useSelector(userSelectors.userRights);

  const textareaRef = useRef();

  const toggleEditMode = useCallback(() => {
    setEditMode(!editMode);
  }, [setEditMode, editMode]);

  const saveNoteCB = useCallback(() => {
    if (!textareaRef.current) {
      throw new Error('Note ref missing');
    }
    const newMessage = textareaRef.current.value;

    if (newMessage !== message) {
      // here, we should optimistically save the new value in the store, and PUT note
      dispatch(dataActions.userEditedNote({ noteId, newMessage, index }));
    }
    toggleEditMode();
  }, [toggleEditMode, message, dispatch, noteId, index]);

  const editButtonAvailable = useMemo(
    () =>
      !moved && userRights.canEditNotes && currentUserEmail === author?.email,
    [userRights.canEditNotes, author, moved, currentUserEmail]
  );

  const buttonLabel = buttonLabelByEditMode[editMode];
  const buttonIcon = buttonIconByLabel[editMode];

  const editOrSaveOnClick = editMode ? saveNoteCB : toggleEditMode;

  if (!message || !userRights.canReadNotes) return null;

  const handleNoteChange = () => {
    if (textareaRef.current.value === '') setDisabledButton(true);
    else setDisabledButton(false);
  };

  return (
    <LogEventDetailsWrapper>
      {editMode ? (
        // eslint-disable-next-line jsx-a11y/no-autofocus
        <StyledTextArea
          ref={textareaRef}
          autoFocus
          defaultValue={message}
          onChange={handleNoteChange}
        />
      ) : (
        <NotesText edited={Boolean(edited)}>{message}</NotesText>
      )}
      {editButtonAvailable && (
        <IconButton
          css="text-transform: capitalize;"
          icon={buttonIcon}
          placement="left"
          onClick={editOrSaveOnClick}
          disabled={disabledButton}
        >
          {buttonLabel}
        </IconButton>
      )}

      {!moved && editMode && userRights.canEditNotes && (
        <IconButton
          css="margin-left: 15px;"
          icon={<FaBan />}
          placement="left"
          onClick={toggleEditMode}
        >
          Cancel
        </IconButton>
      )}

      {moved && <NoteLinkingMessage />}
    </LogEventDetailsWrapper>
  );
}
